/* Offer chip measurement — clicks and their denominator.
 *
 * ES2019 only (FB/IG in-app WebViews) — no optional chaining / nullish coalescing.
 * Shopify re-minifies uploaded asset JS and rewrites `x != null ? x : y` into `??`,
 * so scripts/validate-es2019.mjs bans that source shape too.
 *
 * WHAT IT MEASURES. The chip is a <span> inside a card, not a control — she
 * clicks the CARD. So the click event is "a card carrying an offer chip was
 * clicked", which is the question worth answering: do chipped cards earn the tap
 * more often than plain ones?
 *
 * A click count alone cannot answer that, so an impression event carries the
 * denominator: how many chips and how many cards were on the page. Without it
 * there is a numerator and nothing to divide it by, and the experiment is
 * unreadable six weeks later.
 *
 * DELEGATED FROM document: facets.js replaces #ProductGridContainer.innerHTML
 * on every filter change, so anything bound to a card dies on the first tap of
 * a filter. It also means one listener
 * covers all four snippets that render the chip (product-card-item,
 * product-card-list-item, compare-product-card, deals-product-card) without
 * knowing anything about them.
 *
 * Events reach GA4 through window.seamlessTrack (assets/seamless-track.js) as
 * `offer_chip_click` and `offer_chip_impression`. NOTE: GA4 shows the event
 * count straight away, but the parameters below stay invisible in reports until
 * each is registered as a custom dimension under Admin > Custom definitions.
 */
(function () {
  'use strict';

  var CHIP = '[data-offer-chip]';
  var PRODUCT_LINK = 'a[href*="/products/"]';

  function track(name, params) {
    if (typeof window.seamlessTrack !== 'function') return;
    window.seamlessTrack('offer_chip', name, params);
  }

  /* Works on /collections/x and /ar/collections/x alike, and returns '' on the
     home page and search, where the chip also renders but there is no
     collection to attribute it to. */
  function collectionHandle() {
    var match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function handleFromHref(href) {
    var match = href.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  /* The chip belonging to the clicked card, or null if that card has none.
     Walks up rather than matching a wrapper class, so a template that names its
     card something else is still measured.

     The walk STOPS as soon as a container holds a second product, because that
     container is the grid, not the card. Without that stop the ascent runs past
     an unchipped card into the grid and finds a NEIGHBOUR's chip — measured:
     clicking a plain card scored a chip click, which would have inflated the
     numerator with exactly the cards the experiment exists to compare against. */
  function chipForLink(link) {
    var node = link;
    while (node && node !== document.body) {
      if (countUniqueProducts(node) > 1) return null;
      var chip = node.querySelector(CHIP);
      if (chip) return chip;
      node = node.parentElement;
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest(PRODUCT_LINK);
    if (!link) return;

    var chip = chipForLink(link);
    if (!chip) return;

    /* Fired before navigation. seamlessTrack pushes synchronously into dataLayer
       and gtag; a beacon lost on a slow connection costs a data point, never the
       navigation. */
    track('click', {
      chip: chip.getAttribute('data-offer-chip') || '',
      product: handleFromHref(link.getAttribute('href') || ''),
      collection: collectionHandle(),
    });
  });

  function countUniqueProducts(scope) {
    var seen = {};
    var total = 0;
    var links = scope.querySelectorAll(PRODUCT_LINK);
    for (var i = 0; i < links.length; i++) {
      var handle = handleFromHref(links[i].getAttribute('href') || '');
      if (!handle || seen[handle]) continue;
      seen[handle] = true;
      total++;
    }
    return total;
  }

  function reportImpressions(reason) {
    var chips = document.querySelectorAll(CHIP).length;
    if (chips === 0) return;

    var grid = document.getElementById('ProductGridContainer');
    track('impression', {
      chips: chips,
      cards: countUniqueProducts(grid || document),
      collection: collectionHandle(),
      reason: reason,
    });
  }

  reportImpressions('load');

  /* Filtering swaps the whole card set, so the load-time denominator no longer
     describes what she is looking at. Reported separately rather than merged, so
     a filtered view can be excluded from the rate if it turns out to skew it.

     A MutationObserver on the container rather than a hook into facets.js: the
     grid is replaced by innerHTML assignment, and observing the result keeps
     this file from depending on how that happens. */
  var grid = document.getElementById('ProductGridContainer');
  if (grid && window.MutationObserver) {
    var pending = null;
    new window.MutationObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        reportImpressions('filter');
      }, 300);
    }).observe(grid, { childList: true, subtree: true });
  }
})();
